import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type LoaderFunctionArgs, type ActionFunctionArgs } from '@remix-run/node'
import { Form, json, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Button } from '#app/components/buttons/button.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { H1 } from '#app/components/typography/h1.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Input } from '#app/old/components/data-entry/input.tsx'
import { Label } from '#app/old/components/data-entry/label.tsx'
import { CenteredFormContainer } from '#app/old/patterns/centered-form-container.tsx'
import { FormField } from '#app/old/patterns/form-field.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { validateRequest } from './verify.server.ts'

export const codeQueryParam = 'code'
export const targetQueryParam = 'target'
export const typeQueryParam = 'type'
export const redirectToQueryParam = 'redirectTo'
const types = ['onboarding', 'reset-password', 'change-email', '2fa'] as const
const VerificationTypeSchema = z.enum(types)
export type VerificationTypes = z.infer<typeof VerificationTypeSchema>

export const VerifySchema = z.object({
	code: z.string({ required_error: 'Code is required' }).min(6, { message: 'Code must have 6 characters' }).max(6),
	type: VerificationTypeSchema,
	target: z.string(),
	redirectTo: z.string().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	const params = new URL(request.url).searchParams
	if (!params.has('code')) {
		// we don't want to show an error message on page load if the otp hasn't be
		// prefilled in yet, so we'll send a response with an empty submission.
		return json({
			status: 'idle',
			submission: {
				intent: '',
				payload: Object.fromEntries(params),
				error: {},
			},
		} as const)
	}
	return validateRequest(request, params)
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	checkHoneypot(formData)
	return validateRequest(request, formData)
}

export default function VerifyRoute() {
	const [searchParams] = useSearchParams()
	const isPending = useIsPending()
	const lastResult = useActionData<typeof action>()
	const parsedType = VerificationTypeSchema.safeParse(searchParams.get(typeQueryParam))
	const verificationType = parsedType.success ? parsedType.data : null

	const [form, { code, redirectTo, target, type }] = useForm({
		id: 'verify-form',
		constraint: getZodConstraint(VerifySchema),
		lastResult,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: VerifySchema })
		},
		defaultValue: {
			code: searchParams.get(codeQueryParam) ?? '',
			type: verificationType,
			target: searchParams.get(targetQueryParam) ?? '',
			redirectTo: searchParams.get(redirectToQueryParam) ?? '',
		},
	})

	return (
		<>
			<CenteredFormContainer>
				{verificationType === 'onboarding' || verificationType === 'reset-password' || verificationType === 'change-email' ?
					<div>
						<H1 className="text-muted-700 text-center">Check your email</H1>
						<P className="text-center">We've sent you a code to verify your email address.</P>
					</div>
				: verificationType === '2fa' ?
					<div>
						<H1 className="text-muted-700 text-center">Check your 2FA app</H1>
						<P className="text-center">Please enter your 2FA code to verify your identity.</P>
					</div>
				:	<>
						<H1 className="text-danger-title text-center">Invalid Verification Type</H1>
					</>
				}
				{verificationType ?
					<Form method="POST" {...getFormProps(form)}>
						<HoneypotInputs />
						<Input {...getInputProps(type, { type: 'hidden' })} />
						<Input {...getInputProps(target, { type: 'hidden' })} />
						<Input {...getInputProps(redirectTo, { type: 'hidden' })} />
						<Flex orientation="vertical" gap="10">
							<FormField
								className="max-w-[320px] min-w-[50%] flex-1"
								label={<Label htmlFor={code.id}>Code</Label>}
								input={
									<Input
										aria-invalid={code.errorId ? true : undefined}
										aria-describedby={code.errorId}
										{...getInputProps(code, { type: 'text' })}
										autoFocus
										autoComplete="one-time-code"
										placeholder="Enter your code"
									/>
								}
								errors={code.errors}
							/>
							<Button variant="primary" size="md" type="submit" isDisabled={isPending}>
								{isPending ? 'Verifying code' : 'Verify code'}
							</Button>
						</Flex>
					</Form>
				:	null}
			</CenteredFormContainer>
		</>
	)
}
